import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Edit, X, Save, Upload as UploadIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook: Loads CMS content for a page_key, merges with defaults.
 * Returns: { content, reload, save, uploadMedia }
 */
export const usePageContent = (pageKey, defaults = {}) => {
  const [content, setContent] = useState(defaults);
  const { getToken } = useAuth();

  const reload = async () => {
    try {
      const res = await axios.get(`${API}/settings/page/${pageKey}`);
      setContent({ ...defaults, ...(res.data.fields || {}) });
    } catch { /* keep defaults */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pageKey]);

  const save = async (fields) => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/settings/page/${pageKey}`,
        { fields: { ...content, ...fields } },
        { headers: { Authorization: `Bearer ${token}` } });
      setContent({ ...defaults, ...(res.data.fields || {}) });
      return true;
    } catch { toast.error("Kunne ikke gemme"); return false; }
  };

  const uploadMedia = async (file) => {
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/settings/page/${pageKey}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const fullUrl = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      return { url: fullUrl, isVideo: res.data.is_video };
    } catch {
      toast.error("Kunne ikke uploade");
      return null;
    }
  };

  return { content, reload, save, uploadMedia };
};

/**
 * Generic editor modal that renders form inputs based on a schema.
 * schema = [{ name, label, type: 'text'|'textarea'|'url'|'image'|'video'|'link', placeholder? }]
 */
export const PageEditor = ({ pageKey, schema, defaults, onSaved }) => {
  const { isModerator } = useAuth();
  const { content, save, uploadMedia } = usePageContent(pageKey, defaults);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(null);

  if (!isModerator) return null;

  const startEdit = () => { setForm({ ...content }); setOpen(true); };

  const handleSave = async () => {
    const ok = await save(form);
    if (ok) {
      setOpen(false);
      toast.success("Gemt!");
      onSaved && onSaved();
    }
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    const result = await uploadMedia(file);
    setUploading(null);
    if (result) {
      setForm(p => ({ ...p, [field]: result.url }));
      toast.success("Uploadet!");
    }
  };

  return (
    <>
      <button
        onClick={startEdit}
        className="fixed bottom-5 right-5 z-50 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-2 text-sm font-bold font-['Oswald'] uppercase tracking-wider"
        style={{ boxShadow: "0 0 20px rgba(59, 130, 246, 0.6)" }}
        data-testid={`page-editor-btn-${pageKey}`}
      >
        <Edit size={16} /> Rediger side
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/85 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid={`page-editor-modal-${pageKey}`}
        >
          <div className="min-h-full flex items-start justify-center p-3">
            <div className="my-6 w-full max-w-2xl bg-neutral-950/98 border border-blue-500/30 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-['Oswald'] font-bold text-white uppercase">Rediger side</h2>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
              </div>

              {schema.map((f) => (
                <div key={f.name} className="space-y-1">
                  <label className="block text-xs text-blue-300 uppercase tracking-wider">{f.label}</label>
                  {f.type === "textarea" && (
                    <textarea
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                      rows={4}
                      placeholder={f.placeholder}
                      className="mc-input w-full text-sm resize-none"
                      data-testid={`page-field-${f.name}`}
                    />
                  )}
                  {(f.type === "text" || f.type === "url" || f.type === "link") && (
                    <input
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="mc-input w-full"
                      data-testid={`page-field-${f.name}`}
                    />
                  )}
                  {(f.type === "image" || f.type === "video") && (
                    <>
                      {form[f.name] && f.type === "image" && (
                        <img src={form[f.name]} alt="" className="max-h-32 rounded mb-1 border border-white/10" />
                      )}
                      {form[f.name] && f.type === "video" && (
                        <video src={form[f.name]} controls className="max-h-32 rounded mb-1 border border-white/10" />
                      )}
                      <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs cursor-pointer">
                        <UploadIcon size={12} />
                        {uploading === f.name ? "Uploader..." : `Upload ${f.type === "video" ? "video" : "billede"}`}
                        <input
                          type="file"
                          accept={f.type === "video" ? "video/*" : "image/*"}
                          onChange={(e) => handleFileUpload(f.name, e.target.files?.[0])}
                          className="hidden"
                          data-testid={`page-upload-${f.name}`}
                        />
                      </label>
                      <input
                        value={form[f.name] ?? ""}
                        onChange={(e) => setForm(p => ({ ...p, [f.name]: e.target.value }))}
                        placeholder="…eller URL"
                        className="mc-input w-full text-xs mt-1"
                      />
                    </>
                  )}
                </div>
              ))}

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-neutral-950 py-3">
                <button onClick={handleSave} className="chrome-btn px-4 py-2 rounded-lg flex items-center gap-2" data-testid={`page-save-${pageKey}`}>
                  <Save size={16} /> Gem ændringer
                </button>
                <button onClick={() => setOpen(false)} className="ghost-blue-btn px-4 py-2 rounded-lg flex items-center gap-2">
                  <X size={16} /> Annuller
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
